import React, { useState } from 'react';
import { useSession } from '@/hooks/useSessionHook';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import TopHeader from '@/components/TopHeader';
import BottomNav from '@/components/BottomNav';
import supabase from '@/supabase';

interface SearchResult {
  id: string;
  display_name: string;
  avatar_url: string | null;
  similarity: number;
  friend_status: 'friend' | 'request-sent' | 'none' | 'self';
}

const SearchPage = () => {
  const { session } = useSession();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Grouped results state
  const [results, setResults] = useState({
    friends: [] as SearchResult[],
    sentRequests: [] as SearchResult[],
    searchMatches: [] as SearchResult[],
  });

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Check if session is valid
      if (!session?.user?.id) {
        throw new Error('User session not available');
      }

      // Call the fuzzy_search function with proper authentication context
      const { data, error } = await supabase
        .rpc('fuzzy_search', { search_term: searchTerm });

      if (error) throw error;

      // Group results by friend status
      const grouped = {
        friends: data.filter((r: SearchResult) => r.friend_status === 'friend'),
        sentRequests: data.filter((r: SearchResult) => r.friend_status === 'request-sent'),
        searchMatches: data.filter((r: SearchResult) => r.friend_status === 'none' || r.friend_status === 'self'),
      };

      setResults(grouped);
    } catch (err: unknown) {
      console.error('Search error:', err);
      let errorMessage = 'Failed to perform search';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const sendFriendRequest = async (recipientId: string) => {
    if (!session?.user?.id) return;

    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: session.user.id,
          recipient_id: recipientId,
          status: 'pending'
        });

      if (error) throw error;

      // Notify recipient
      let broadcastChannel;
      try {
        broadcastChannel = supabase.channel(`realtime-broadcast-${recipientId}`);
        await broadcastChannel.subscribe();
        await broadcastChannel.send({
          type: 'broadcast',
          event: 'new-friend-request',
          payload: { senderId: session.user.id }
        });
      } catch (broadcastError) {
        console.error('Error sending broadcast notification:', broadcastError);
      } finally {
        // Ensure channel is always cleaned up, even if error occurs
        if (broadcastChannel) {
          await supabase.removeChannel(broadcastChannel);
        }
      }

      // Update UI to show request sent
      setResults(prev => ({
        ...prev,
        searchMatches: prev.searchMatches.map(user =>
          user.id === recipientId ? { ...user, friend_status: 'request-sent' } : user
        )
      }));
    } catch (err) {
      console.error('Friend request error:', err);
      setError('Failed to send friend request.');
    }
  };

  // Render section for each group
  const renderSection = (title: string, profiles: SearchResult[], key: string) => {
    if (profiles.length === 0) return null;
    
    return (
      <div key={key} className="mb-6">
        <h3 className="font-semibold text-lg mb-3">{title}</h3>
        <div className="space-y-3">
          {profiles.map((user) => (
            <div key={user.id} className="bg-white rounded-lg p-4 flex items-center shadow-sm">
              <Avatar className="h-12 w-12 mr-4">
                {user.avatar_url ? (
                  <AvatarImage src={user.avatar_url} alt={user.display_name} />
                ) : (
                  <AvatarFallback>
                    {user.display_name.charAt(0)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1">
                <h3 className="font-medium">{user.display_name}</h3>
                <p className="text-sm text-gray-500">
                  Match: {(user.similarity * 100).toFixed(1)}%
                </p>
              </div>
              
              {user.friend_status === 'friend' && (
                <Button variant="secondary" disabled>Friends</Button>
              )}
              
              {user.friend_status === 'request-sent' && (
                <Button variant="secondary" disabled>Request Sent</Button>
              )}
              
              {user.friend_status === 'self' && (
                <Button variant="secondary" disabled>You</Button>
              )}

              {user.friend_status === 'none' && (
                <Button onClick={() => sendFriendRequest(user.id)}>Add Friend</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <TopHeader />
      
      <main className="flex-1 overflow-y-auto p-4">
        <form onSubmit={handleSearch} className="flex mb-4">
          <Input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 rounded-r-none"
          />
          <Button 
            type="submit"
            disabled={isLoading}
            className="rounded-l-none"
          >
            Search
          </Button>
        </form>
        
        {error && (
          <div className="text-red-500 text-center mb-4">{error}</div>
        )}
        
        {isLoading && (
          <div className="text-center py-4">Searching...</div>
        )}
        
        {!isLoading && searchTerm && (
          <div>
            {renderSection("Your Friends", results.friends, "friends")}
            {renderSection("Sent Requests", results.sentRequests, "sent")}
            {renderSection("Search Results", results.searchMatches, "results")}
            
            {results.friends.length === 0 && 
             results.sentRequests.length === 0 && 
             results.searchMatches.length === 0 && (
              <div className="text-center py-4">No results found</div>
            )}
          </div>
        )}
        
        {/* Show friends and sent requests when no search term */}
        {!isLoading && !searchTerm && (
          <div>
            {renderSection("Your Friends", results.friends, "friends-initial")}
            {renderSection("Sent Requests", results.sentRequests, "sent-initial")}
            
            {results.friends.length === 0 && 
             results.sentRequests.length === 0 && (
              <div className="text-center py-4">Search for users to add friends</div>
            )}
          </div>
        )}
      </main>
      
      <BottomNav />
    </div>
  );
};

export default SearchPage;
